import { logWarning } from '@core/logging';
import { lineProbeHub, useAppSelector } from '@online-webinar/store';
import { useEffect, useRef } from 'react';
import { LineProbeController, selectLineProbeState } from './lineProbeController';

/**
 * Measures the active presenter's line silently during a broadcast (spec 8) and reports it through
 * the live hub. Shows nothing, never touches the live status and never throws. The controller lives
 * in an effect, so never during server-side rendering; unmounting the page stops it.
 */
export function useLineProbe(): void {
    // One primitive per selector, so the page re-renders only when one of them changes.
    const joined = useAppSelector((state) => selectLineProbeState(state).joined);
    const isActivePresenter = useAppSelector((state) => selectLineProbeState(state).isActivePresenter);
    const hubConnected = useAppSelector((state) => selectLineProbeState(state).hubConnected);
    const webinarEnded = useAppSelector((state) => selectLineProbeState(state).webinarEnded);

    const controllerRef = useRef<LineProbeController>(undefined);

    useEffect(() => {
        const controller = new LineProbeController({
            hub: lineProbeHub,
            warn: (message, parameters) => logWarning(message, parameters)
        });
        controllerRef.current = controller;
        return () => {
            controllerRef.current = undefined;
            controller.dispose();
        };
    }, []);

    useEffect(() => {
        controllerRef.current?.update({ joined, isActivePresenter, hubConnected, webinarEnded });
    }, [joined, isActivePresenter, hubConnected, webinarEnded]);
}
